// Qué sincronizaciones de terreno se RESALTAN en las pestañas del editor.
//
// Vive a nivel del editor (no de cada pestaña) porque la elección es una sola:
// quien resalta "la del 05 jul" en Maquinaria la sigue viendo al pasar a
// Instalaciones. Las pestañas NUNCA esconden ni reordenan por esto; solo marcan.
//
// Sin proveedor el valor es "nada resaltado", que es lo que necesita todo test
// que dibuje una pestaña suelta. La pestaña "Registro de terreno" FILTRA, no
// resalta: ninguna de las piezas que dibuja lee este contexto.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import { horaDeSincronizacion } from "@/components/informes/sincronizaciones";
import {
  resaltadasDe,
  resaltadoDelItem,
  type ResaltadoDeUnaFila,
} from "@/components/informes/resaltado";
import type { ItemDeTerreno, SincronizacionDeTerreno } from "@/lib/terreno";

export interface ResaltadoDeSincronizaciones {
  sincronizaciones: SincronizacionDeTerreno[];
  /** `null` = nadie eligió (se resaltan las nuevas); cualquier arreglo es una elección manual. */
  seleccion: string[] | null;
  resaltadas: ReadonlySet<string>;
  /** Una captura anterior a las sincronizaciones (`null`) nunca se resalta. */
  esResaltada: (id: string | null | undefined) => boolean;
  /** "09:12": la etiqueta corta de "SINC. 09:12". */
  etiqueta: (id: string) => string;
  deItem: (item: ItemDeTerreno | null | undefined) => ResaltadoDeUnaFila;
  elegir: (ids: string[]) => void;
  /** "Marcar como revisado" y "Quitar resaltado": la elección pasa a ser «ninguna». */
  reiniciar: () => void;
}

export const SIN_RESALTADO: ResaltadoDeSincronizaciones = {
  sincronizaciones: [],
  seleccion: null,
  resaltadas: new Set(),
  esResaltada: () => false,
  etiqueta: () => "—",
  deItem: () => ({ on: false, horas: [] }),
  elegir: () => {},
  reiniciar: () => {},
};

const Contexto = createContext<ResaltadoDeSincronizaciones>(SIN_RESALTADO);

export function ProveedorDeResaltado({
  sincronizaciones,
  children,
}: {
  sincronizaciones: SincronizacionDeTerreno[];
  children: ReactNode;
}) {
  const [seleccion, setSeleccion] = useState<string[] | null>(null);

  const elegir = useCallback((ids: string[]) => setSeleccion(ids), []);
  const reiniciar = useCallback(() => setSeleccion([]), []);

  const valor = useMemo<ResaltadoDeSincronizaciones>(() => {
    const resaltadas = resaltadasDe(seleccion, sincronizaciones);
    const porId = new Map(sincronizaciones.map((s) => [s.id, s]));

    return {
      sincronizaciones,
      seleccion,
      resaltadas,
      esResaltada: (id) => typeof id === "string" && resaltadas.has(id),
      etiqueta: (id) => {
        const s = porId.get(id);

        return s ? horaDeSincronizacion(s.en) : "—";
      },
      deItem: (item) => resaltadoDelItem(item, resaltadas, sincronizaciones),
      elegir,
      reiniciar,
    };
  }, [seleccion, sincronizaciones, elegir, reiniciar]);

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useResaltadoDeSincronizaciones(): ResaltadoDeSincronizaciones {
  return useContext(Contexto);
}
